import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { ArgoContext, useArgoContext } from '@amzn/awsscm-argo-core';
import { RouteLoadDurationProvider } from '@amzn/awsscm-argo-core/argo/utils/metrics/ArgoRouteLoadDurationContext';
import KatalLogger from '@katal/logger';

import Alert from '@amzn/awsui-components-react-v3/polaris/alert';
import Box from '@amzn/awsui-components-react-v3/polaris/box';
import Spinner from '@amzn/awsui-components-react-v3/polaris/spinner';

import { RFXAppLayout } from '../../components/RFXAppLayout';
import { getConfigValue } from '../../config/appConfig';
import { useUserBusiness, UseUserBusinessResultType } from '../hooks/useUserBusiness';
import PageAccessAlert from './PageAccessAlert';
import {
  getDevUserPermissions,
  getUserPermissions,
  isExternalArgoUser,
  UserPermissionProps
} from './userPermissionHelper';

// Lazy loading rfxportal.pages allows our build system to split up the code into different
// bundles, so customers do not have to download entire app at once. You can
// import these as normal if you do not need to split up code bundles between rfxportal.pages.
// Learn more https://reactjs.org/docs/code-splitting.html#route-based-code-splitting

const BuyerDashboard = React.lazy(() => import('../pages/BuyerDashboard/index'));
const CreateRFXRequest = React.lazy(() => import('../pages/RFXRequest/createrequest/index'));
const CloneRFXRequest = React.lazy(() => import('../pages/RFXRequest/clonerequest/index'));
const BuyerRFQDetailView = React.lazy(() => import('../pages/ViewPages/BuyerRFXDetailView/BuyerRFQDetailView'));
const BuyerRFIDetailView = React.lazy(() => import('../pages/ViewPages/BuyerRFXDetailView/BuyerRFIDetailView'));
const SideBySideComparison = React.lazy(() => import('../pages/ComparePage/SideBySideComparison'));
const OfferDetailView = React.lazy(() => import('../pages/OfferPage/OfferDetailView'));
const EditRFXRound = React.lazy(() => import('../pages/RFXRequest/editround/index'));
const InitiateRFXRound = React.lazy(() => import('../pages/RFXRequest/initiateround/index'));
const SupplierDashboard = React.lazy(() => import('../pages/SupplierDashboard/index'));
const SupplierRFXDetail = React.lazy(() => import('../pages/ViewPages/SupplierRFXDetailView/SupplierRFXDetailView'));
const SupplierRFIDetail = React.lazy(() => import('../pages/ViewPages/SupplierRFXDetailView/SupplierRFIDetailView'));
const EditRFXResponse = React.lazy(() => import('../pages/RFXResponse/editresponse/index'));
const InitiateRFXResponse = React.lazy(() => import('../pages/RFXResponse/initiateresponse/index'));
const UserNotificationDashboard = React.lazy(() => import('../pages/Notifications/index'));
const PostAward = React.lazy(() => import('../pages/PostAward/index'));
const CreateRFIRequest = React.lazy(() => import('../pages/RFXRequest/createrfi/'));
const EditRFIRequest = React.lazy(() => import('../pages/RFXRequest/editrfi/'));
const CloneRFIRequest = React.lazy(() => import('../pages/RFXRequest/clonerfi/'));
const CreateChangeOrder = React.lazy(() => import('../pages/ChangeOrder/create/index'));
const ChangeOrderDetailView = React.lazy(() => import('../pages/ChangeOrder/view/user/index'));
const ChangeOrderDashboard = React.lazy(() => import('../pages/ChangeOrder/dashboard/index'));

const noBusinessMessage = 'Cannot find user business. Please add user to a business before continuing!';

const logUserPermissions = (props: UserPermissionProps, logger: KatalLogger) => {
  const {
    customGroups,
    userName,
    isExternalUser,
    shouldAccessFullBuyerExp,
    shouldAccessLimitedBuyerExp,
    shouldAccessFullSupplierExp
  } = props;
  logger.info(
    `Cred Storage Username: ${userName}; ` +
      `Cred Storage LDAP Groups: ${customGroups}; ` +
      `Is External User: ${isExternalUser}; ` +
      `Should Access Full Buyer Exp: ${shouldAccessFullBuyerExp}; ` +
      `Should Access Limited Buyer Exp: ${shouldAccessLimitedBuyerExp}; ` +
      `Should Access Full Supplier Exp: ${shouldAccessFullSupplierExp}`
  );
};

/**
 * @returns Component that redirects user to an unauthorized page when they do
 * not have permission to go to a link.
 */
const RedirectToUnauthorizedPage = () => {
  console.log('User not authorized to access selected page, redirecting.');
  return <Redirect to="/unauthorized" />;
};

const RoutingTable = () => {
  const context: ArgoContext = useArgoContext();

  const userPermissions: UserPermissionProps =
    window.location.hostname === 'localhost'
      ? // For localhost
        getDevUserPermissions(isExternalArgoUser())
      : // For prod, or personal portal
        getUserPermissions();
  logUserPermissions(userPermissions, context.logger);

  const useBusinessResult: UseUserBusinessResultType = useUserBusiness();

  const { loading, error, userBusiness, isExternalUser } = useBusinessResult;
  context.logger.info(`Username: ${userPermissions.userName} with UserBusiness: ${JSON.stringify(userBusiness)}`);
  const shouldShowNoBusinessMessage: boolean = !isExternalUser && (userBusiness === null || userBusiness.length === 0);
  const { shouldAccessFullSupplierExp, shouldAccessLimitedBuyerExp, shouldAccessFullBuyerExp } = userPermissions;

  if (loading) {
    return (
      <Box color="text-status-inactive">
        <Spinner /> Loading
      </Box>
    );
  }
  if (error) {
    return <RFXAppLayout content={<Alert type="warning">{error}</Alert>} />;
  }
  if (shouldShowNoBusinessMessage) {
    return <RFXAppLayout content={<Alert type="warning">{noBusinessMessage}</Alert>} />;
  }

  const renderMain = () => {
    if (isExternalUser && shouldAccessFullSupplierExp) {
      return <SupplierDashboard />;
    }
    if (!isExternalUser && shouldAccessLimitedBuyerExp) {
      return <BuyerDashboard />;
    }
    return RedirectToUnauthorizedPage;
  };

  return (
    <RouteLoadDurationProvider>
      <React.Suspense
        fallback={
          <RFXAppLayout
            content={
              <Box color="text-status-inactive">
                <Spinner /> Loading
              </Box>
            }
          />
        }
      >
        <Switch>
          <Route exact path={'/unauthorized'}>
            <PageAccessAlert />
          </Route>

          <Route exact path={'/'}>
            {renderMain()}
          </Route>

          <Route exact path={'/buyer/postaward/:rfxId/:revision?'}>
            {<PostAward />}
          </Route>

          <Route exact path={'/buyer/dashboard'}>
            {shouldAccessLimitedBuyerExp ? <BuyerDashboard /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/view/:rfxId'}>
            {shouldAccessLimitedBuyerExp ? <BuyerRFQDetailView /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/compare/:rfxId/:revision?'}>
            {shouldAccessLimitedBuyerExp ? <SideBySideComparison /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/offer/:rfxId/:revision?'}>
            {shouldAccessLimitedBuyerExp ? <OfferDetailView /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/createrequest'}>
            {shouldAccessFullBuyerExp ? <CreateRFXRequest /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/clonerequest'}>
            {shouldAccessFullBuyerExp ? <CloneRFXRequest /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/view/:rfxId/editround'}>
            {shouldAccessFullBuyerExp ? <EditRFXRound /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/view/:rfxId/initiateround'}>
            {shouldAccessFullBuyerExp ? <InitiateRFXRound /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/supplier/dashboard'}>
            {shouldAccessFullSupplierExp ? <SupplierDashboard /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/supplier/view/:rfxId'}>
            {shouldAccessFullSupplierExp ? <SupplierRFXDetail /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/supplier/view/:rfxId/editresponse'}>
            {shouldAccessFullSupplierExp ? <EditRFXResponse /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/supplier/view/:rfxId/initiateresponse'}>
            {shouldAccessFullSupplierExp ? <InitiateRFXResponse /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/notifications'}>
            {shouldAccessFullBuyerExp ? <UserNotificationDashboard /> : RedirectToUnauthorizedPage}
          </Route>

          {/* RFI */}

          <Route exact path={'/buyer/createrfi'}>
            {shouldAccessFullBuyerExp ? <CreateRFIRequest /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/view/rfi/:rfxId'}>
            {shouldAccessLimitedBuyerExp ? <BuyerRFIDetailView /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/view/rfi/:rfxId/edit'}>
            {shouldAccessFullBuyerExp ? <EditRFIRequest /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/supplier/view/rfi/:rfxId'}>
            {shouldAccessFullSupplierExp ? <SupplierRFIDetail /> : RedirectToUnauthorizedPage}
          </Route>

          <Route exact path={'/buyer/clonerfi'}>
            {shouldAccessFullBuyerExp ? <CloneRFIRequest /> : RedirectToUnauthorizedPage}
          </Route>

          {/* Change Order */}

          { getConfigValue('enableChangeOrder') && (
              <>
                <Route exact path={'/changeorder/create'}>
                  {shouldAccessFullBuyerExp || shouldAccessFullSupplierExp ? <CreateChangeOrder /> : RedirectToUnauthorizedPage}
                </Route>

                <Route exact path={'/changeorder/view/:coId'}>
                  {shouldAccessFullBuyerExp || shouldAccessFullSupplierExp ? <ChangeOrderDetailView /> : RedirectToUnauthorizedPage}
                </Route>

                <Route exact path={'/changeorder/dashboard'}>
                  {shouldAccessFullBuyerExp || shouldAccessFullSupplierExp ? <ChangeOrderDashboard /> : RedirectToUnauthorizedPage}
                </Route>
              </>
          )}

        </Switch>
      </React.Suspense>
    </RouteLoadDurationProvider>
  );
};

export default RoutingTable;
